export type ConnectionState = "ready" | "connecting" | "waking" | "unavailable";

const IDLE_CHECK_MS = 60_000;
const WAKE_TIMEOUT_MS = 120_000;
const PROBE_TIMEOUT_MS = 10_000;
const RETRY_DELAY_MS = 3_000;
const temporaryStatus = (status: number) => [502, 503, 504].includes(status);
const abortError = () => new DOMException("Request cancelled", "AbortError");

/** Readiness checks happen on demand, never as a background keep-alive. */
export function createBackendConnection(
  baseUrl: string,
  onState: (state: ConnectionState) => void,
) {
  let lastResponseAt: number | null = null;
  let pending: Promise<void> | null = null;

  const wake = async () => {
    lastResponseAt = null;
    onState("connecting");
    const notice = window.setTimeout(() => onState("waking"), 2_000);
    const deadline = Date.now() + WAKE_TIMEOUT_MS;
    try {
      while (Date.now() < deadline) {
        const controller = new AbortController();
        const timeout = window.setTimeout(
          () => controller.abort(),
          Math.min(PROBE_TIMEOUT_MS, deadline - Date.now()),
        );
        let response: Response | undefined;
        try {
          response = await fetch(`${baseUrl}/health`, {
            signal: controller.signal,
            cache: "no-store",
          });
        } catch {
          // Cold starts can fail at the proxy or before a connection exists.
        } finally {
          window.clearTimeout(timeout);
        }
        if (response?.status === 200) {
          lastResponseAt = Date.now();
          onState("ready");
          return;
        }
        if (response && !temporaryStatus(response.status)) {
          throw new Error("Unable to connect to the server. Please try again.");
        }
        const remaining = deadline - Date.now();
        if (remaining > 0) {
          await new Promise((resolve) =>
            window.setTimeout(resolve, Math.min(RETRY_DELAY_MS, remaining)),
          );
        }
      }
      throw new Error(
        "The server is taking longer than expected. Please try again.",
      );
    } catch (error) {
      onState("unavailable");
      throw error;
    } finally {
      window.clearTimeout(notice);
    }
  };

  const ensureReady = async (force = false) => {
    if (pending) return pending;
    if (
      !force &&
      lastResponseAt !== null &&
      Date.now() - lastResponseAt < IDLE_CHECK_MS
    )
      return;
    // All simultaneous page requests share one bounded wake-up sequence.
    pending = wake().finally(() => {
      pending = null;
    });
    return pending;
  };

  return {
    async fetch(path: string, init?: RequestInit): Promise<Response> {
      if (init?.signal?.aborted) throw abortError();
      await ensureReady();
      if (init?.signal?.aborted) throw abortError();
      const readOnly = ["GET", "HEAD"].includes(
        (init?.method ?? "GET").toUpperCase(),
      );
      let response: Response;
      try {
        response = await fetch(`${baseUrl}${path}`, init);
      } catch (error) {
        if (
          !readOnly ||
          init?.signal?.aborted ||
          (error instanceof Error && error.name === "AbortError")
        )
          throw error;
        await ensureReady(true);
        if (init?.signal?.aborted) throw abortError();
        return fetch(`${baseUrl}${path}`, init);
      }
      if (readOnly && temporaryStatus(response.status)) {
        await ensureReady(true);
        if (init?.signal?.aborted) throw abortError();
        return fetch(`${baseUrl}${path}`, init);
      }
      // Even a normal 4xx/5xx response means the server is awake. Pass it to
      // the caller unchanged; never retry validation or authorization failures.
      lastResponseAt = Date.now();
      onState("ready");
      return response;
    },
  };
}
